import { usePageSEO } from "@/lib/usePageSEO"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Link, useNavigate } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"

import { authService } from "@/features/auth/services/authService"
import { useAuthStore } from "@/stores/authStore"
import { APP_NAME } from "@/lib/appConfig"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
})

type FormValues = z.infer<typeof schema>

export default function LoginPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)

  usePageSEO({
    title: `Sign In | ${APP_NAME}`,
    description: `Sign in to your ${APP_NAME} account to manage your rental properties, tenants, and finances.`,
    path: "/login",
    noindex: true,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  })

  const { mutate, isPending } = useMutation({
    mutationFn: ({ email, password }: FormValues) =>
      authService.login(email, password),
    onSuccess: (res) => {
      setAuth(res.user, res.token)
      toast.success("Welcome back!")
      navigate("/dashboard")
    },
    onError: (err: unknown) => {
      const axiosError = err as any
      const message =
        axiosError?.response?.data?.error ??
        (err instanceof Error ? err.message : "Login failed. Please try again.")
      toast.error(message)
    },
  })

  return (
    <div className="min-h-screen w-full flex bg-background">
      {/* Left side: Premium Gradient Mesh (Desktop Only) */}
      <div className="hidden lg:flex w-1/2 relative flex-col justify-between p-12 overflow-hidden bg-foreground">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/30 to-background opacity-40 mix-blend-screen" />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3 text-white">
          <img src="/app-icon.png" className="h-10 w-10 object-contain" alt={APP_NAME} />
          <span className="font-bold text-2xl tracking-tight">{APP_NAME.toUpperCase()}</span>
        </div>

        {/* Glassmorphic Testimonial */}
        <div className="relative z-10 max-w-md">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-3xl shadow-2xl">
            <p className="text-white text-xl font-medium leading-relaxed mb-6">
              "{APP_NAME} has completely transformed how I manage my properties. The premium experience is unmatched."
            </p>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-white/20 border border-white/20" />
              <div>
                <p className="text-white font-semibold">Sarah Jenkins</p>
                <p className="text-white/70 text-sm">Portfolio Manager</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side: Form Details */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center justify-center gap-2 mb-8 text-foreground">
            <img src="/app-icon.png" className="h-10 w-10 object-contain" alt={APP_NAME} />
            <span className="font-bold text-2xl tracking-tight">{APP_NAME.toUpperCase()}</span>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Sign in to your {APP_NAME} account
            </p>
          </div>

          <Card className="rounded-3xl shadow-soft border-border/50 bg-card/60 backdrop-blur-xl">
            <CardContent className="pt-6">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit((v) => mutate(v))}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            autoComplete="email"
                            placeholder="you@example.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Password</FormLabel>
                          <Link
                            to="/forgot-password"
                            className="text-xs text-primary hover:underline"
                          >
                            Forgot password?
                          </Link>
                        </div>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="current-password"
                            placeholder="••••••••"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl mt-4 shadow-glow transition-all duration-200 active:scale-[0.97]"
                    disabled={isPending}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-8">
            No account?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline">
              Create one
            </Link>
          </p>

          <p className="text-center text-xs text-muted-foreground/60 mt-4">
            {APP_NAME} &middot; A product of{" "}
            <a href="https://soulspark.games/" target="_blank" rel="noopener noreferrer" className="hover:underline">
              SoulSpark Games
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
